import {
    calculateEntryMetrics,
    getRoundedCalculations,
    getRoundedQuantity,
} from "@client/calculations";
import {Card, UnitQuantityWidget} from "@client/components/Card";
import {EntryIcon} from "@client/components/EntryIcon";
import * as forms from "@client/forms";
import type {Locator} from "@client/gotterdammerung";
import {useDebounce} from "@client/hooks";
import * as analytics from "@client/shared/analytics";
import {DraggableProvidedDragHandleProps} from "@hello-pangea/dnd";
import {server} from "@reactivated";
import * as React from "react";

export interface Props {
    entry:
        | server.journal.nutrition.ExpandedEntry.output
        | server.journal.nutrition.Entry.output
        | server.journal.nutrition.Recipe.output
        | {
              entry:
                  | server.journal.nutrition.Entry.output
                  | server.journal.nutrition.InlineEntry.output;
              quantity: string | null;
              unit: string;
          };
    // Only used by IngredientCard and SearchResultCard to show calories so useStage can accept either.
    showCalories?: boolean;

    tag?: string;
    extra?: React.ReactNode;
    link?: string | Locator | (() => void);
    indented?: boolean;

    switchHandler?: {
        value: boolean | null;
        onSwitch: () => void;
    };
    removeHandler?: {
        onRemove: () => void;
        isRemoved: boolean;
    };

    quantityAndUnitHandler?: {
        quantity: {
            multiplier?: DecimalValue | null;
            value: DecimalValue | null;
            onChange: (value: DecimalValue | null) => void;
        };
        unit: {
            value: string;
            options: string[];
            onChange: (value: string) => void;
        };
    };

    isDragging?: boolean;

    sorting?: {
        handle: DraggableProvidedDragHandleProps | null;
        isDragging: boolean;
    };

    hiddenMacros: boolean;
}

export const EntryCard = (props: Props) => {
    const useKilojoules = forms.useUseKilojoules();

    const {entry, quantityAndUnitHandler} = props;

    const quantity =
        quantityAndUnitHandler != null
            ? quantityAndUnitHandler.quantity.value
            : entry.quantity;

    const unit =
        quantityAndUnitHandler != null ? quantityAndUnitHandler.unit.value : entry.unit;
    const calculations =
        "entry" in entry
            ? getRoundedCalculations(
                  calculateEntryMetrics({entry: entry.entry, quantity, unit}),
                  useKilojoules === true,
              )
            : getRoundedCalculations(
                  calculateEntryMetrics({entry, quantity, unit}),
                  useKilojoules === true,
              );

    const quantityAndUnitDisplay = {
        quantity: getRoundedQuantity(quantity),
        unit,
    };

    const isProcessing =
        "nutritional_vision_prompt" in entry &&
        entry.nutritional_vision_prompt?.status === "PROCESSING" &&
        entry.nutritional_vision_prompt.image != null
            ? "image"
            : "nutritional_vision_prompt" in entry &&
                entry.nutritional_vision_prompt?.status === "PROCESSING"
              ? "audio"
              : undefined;

    // const [isProcessing, setIsProcessing] = React.useState(true);

    // React.useEffect(() => {
    //     setTimeout(() => {
    //         setIsProcessing(false);
    //     }, 1000);
    // }, []);

    // const isProcessing = calculations.calories == "460";

    return (
        <Card
            {...props}
            tag={props.tag}
            isProcessing={isProcessing}
            item={"entry" in entry ? entry.entry : entry}
            icon={"entry" in entry ? entry.entry.icon : entry.icon}
            macros={calculations}
            quantityAndUnitDisplay={quantityAndUnitDisplay}
            hiddenMacros={props.hiddenMacros}
            photo={
                "nutritional_vision_prompt" in entry &&
                entry.nutritional_vision_prompt?.image?.image_uid != null
                    ? {image_uid: entry.nutritional_vision_prompt.image.image_uid}
                    : undefined
            }
        />
    );
};

export function CardMetrics(props: {
    calculations: ReturnType<typeof getRoundedCalculations>;
}) {
    const {calculations} = props;

    return (
        <div className="@container flex w-full gap-1.5 font-normal whitespace-nowrap *:flex-1 *:truncate *:rounded-md *:px-1 *:py-0.25 *:text-center *:text-[0.688rem]">
            <span className="bg-indigo-100">
                {calculations.protein}g{" "}
                <span>
                    p<span className="hidden @[320px]:inline">rotein</span>
                </span>
            </span>
            <span className="bg-sky-100">
                {calculations.carbs}g{" "}
                <span>
                    c<span className="hidden @[320px]:inline">arbs</span>
                </span>
            </span>
            <span className="bg-pink-100">
                {calculations.fat}g{" "}
                <span>
                    f<span className="hidden @[320px]:inline">at</span>
                </span>
            </span>
        </div>
    );
}

export function IngredientCard(props: Props) {
    const useKilojoules = forms.useUseKilojoules();

    const {entry, quantityAndUnitHandler} = props;

    const quantity =
        quantityAndUnitHandler != null
            ? quantityAndUnitHandler.quantity.value
            : entry.quantity;

    const unit =
        quantityAndUnitHandler != null ? quantityAndUnitHandler.unit.value : entry.unit;
    const calculations =
        "entry" in entry
            ? getRoundedCalculations(
                  calculateEntryMetrics({entry: entry.entry, quantity, unit}),
                  useKilojoules === true,
              )
            : getRoundedCalculations(
                  calculateEntryMetrics({entry, quantity, unit}),
                  useKilojoules === true,
              );

    const initialQuantity = React.useRef(true);
    const initialUnit = React.useRef(true);

    // Unit must come first to cancel the quantity change.
    useDebounce(
        () => {
            if (initialUnit.current == true) {
                initialUnit.current = false;
                return;
            }
            initialQuantity.current = true;

            // Only for ingredients, not search results.
            if (props.showCalories == false) {
                void analytics.track("CHANGED_SHARED_RECIPE", {changed_field: "unit"});
            }
        },
        500,
        [unit],
    );

    useDebounce(
        () => {
            if (initialQuantity.current == true) {
                initialQuantity.current = false;
                return;
            }
            // Only for ingredients, not search results.
            if (props.showCalories == false) {
                void analytics.track("CHANGED_SHARED_RECIPE", {
                    changed_field: "quantity",
                });
            }
        },
        500,
        [quantity],
    );

    const item = "entry" in props.entry ? props.entry.entry : props.entry;
    const roundedQuantity = getRoundedQuantity(item.quantity);

    const renderedQuantityAndUnit =
        props.quantityAndUnitHandler != null ? (
            <UnitQuantityWidget
                quantity={props.quantityAndUnitHandler.quantity}
                unit={props.quantityAndUnitHandler.unit}
                isRemoved={props.removeHandler?.isRemoved}
            />
        ) : (
            <div className="truncate text-sm font-normal text-gray-600">
                {roundedQuantity} {item.unit}
            </div>
        );

    return (
        <button
            className="flex w-full min-w-0 flex-col gap-2 text-left"
            aria-label={item.name}
            onClick={
                props.link == null
                    ? undefined
                    : () => {
                          if (typeof props.link != "function") {
                              throw new Error("Link must be a function");
                          }
                          return props.link();
                      }
            }
        >
            <div className="flex w-full min-w-0 flex-1 items-center gap-4">
                <EntryIcon icon={item.icon} size="card" />
                <span className="flex-1 text-lg/6 font-extralight">{item.name}</span>
                <div
                    onClick={(event) => {
                        // So the unit and quantity are clickable and don't get captured by the full card.
                        if (props.quantityAndUnitHandler != null) {
                            event.stopPropagation();
                        }
                    }}
                    className="min-w-0"
                >
                    {renderedQuantityAndUnit}
                </div>
            </div>
            {props.showCalories == true && (
                <div className="-mt-1.5 truncate text-sm font-normal text-gray-600">
                    {calculations.calories} cal
                </div>
            )}
            <CardMetrics calculations={calculations} />
        </button>
    );
}

export function SearchResultCard(props: Props) {
    return <IngredientCard {...props} showCalories={true} />;
}
